import { Routes, Route, Navigate } from "react-router-dom";

import AdminLayout from "layouts/admin";
import AuthLayout from "layouts/auth";
import "assets/css/Plugins.css";
import { useState, useEffect } from "react";
import Toaster from "providers/toast";
import { useAuth } from "api/hooks/useAuth";
import PrivateRoute from "PrivateRoute";
import AccountsDefault from "views/auth/accounts/AccountsDefault";
import { setupLocalization } from "providers/localization";
import useWhiteLabel from "whitelabel/useWhiteLabel";
import { setFavicon, setTitle } from "util/branding";
import { isDarkMode, getStoredThemeMode, setDarkMode } from "util/theme";

interface ThemeApp {
  [key: string]: string;
}

const App = () => {
  const { colors, translations, applyThemeColors } = useWhiteLabel();
  const [isLoading, setIsLoading] = useState(true);
  const { isAuthenticated, needsAccountSetup } = useAuth();

  // Get the current theme colors based on dark mode
  const getCurrentThemeColors = (): ThemeApp => {
    const isDark = isDarkMode();
    return isDark ? colors.dark : colors.light;
  };

  const [themeApp, setThemeApp] = useState<ThemeApp>(getCurrentThemeColors());

  const [mini, setMini] = useState(false);

  // Initialize theme from localStorage on mount
  useEffect(() => {
    const storedThemeMode = getStoredThemeMode();
    setDarkMode(storedThemeMode);
    applyThemeColors(storedThemeMode);
  }, [applyThemeColors]);

  // When the theme state changes, this effect will update the CSS variables in the document's root element
  useEffect(() => {
    Object.entries(themeApp).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value);
    });
  }, [themeApp]);

  // Apply theme colors when component mounts
  useEffect(() => {
    applyThemeColors(isDarkMode());
  }, [applyThemeColors]);

  const applyBranding = () => {
    setTitle();
    setFavicon();
  };

  useEffect(() => {
    applyBranding();
    //Setup internationalisation
    setupLocalization(translations).then(() => {
      setIsLoading(false);
    });
  }, []);

  if (isLoading) {
    return <div></div>;
  } else {
    return (
      <div>
        <Routes>
          {isAuthenticated ? (
            needsAccountSetup ? (
              <>
                <Route
                  path="auth/accounts/default"
                  element={<AccountsDefault />}
                />
                <Route
                  path="*"
                  element={<Navigate to="auth/accounts/default" replace />}
                />
              </>
            ) : (
              <>
                <Route
                  path="admin/*"
                  element={
                    <PrivateRoute>
                      <AdminLayout
                        setMini={setMini}
                        mini={mini}
                        theme={themeApp}
                        setTheme={setThemeApp}
                      />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/"
                  element={
                    <PrivateRoute>
                      <Navigate to="/admin" replace />
                    </PrivateRoute>
                  }
                />
              </>
            )
          ) : (
            <>
              {/* Route to AuthLayout if not authenticated */}
              <Route path="auth/*" element={<AuthLayout />} />
              <Route path="/" element={<Navigate to="/auth" replace />} />
            </>
          )}

          {/* Optional: Handle other routes or 404 */}
          <Route
            path="*"
            element={
              <Navigate to={isAuthenticated ? "/admin" : "/auth"} replace />
            }
          />
        </Routes>

        <Toaster />
      </div>
    );
  }
};

export default App;
